import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Save, Send, CheckCircle } from 'lucide-react';
import Button from '../../components/ui/Button';
import { useSidebar } from '../../hooks/useSidebar';
import Icon from '../../components/ui/Icon';

import ClassificationSection from './components/ClassificationSection';
import ClassificationPathway from './components/ClassificationPathway';
import LocationTimeSection from './components/LocationTimeSection';
import PersonnelSection from './components/PersonnelSection';
import IncidentDescription from './components/IncidentDescription';
import HazardCategory from './components/HazardCategory';
import ImmediateActions from './components/ImmediateActions';
import WitnessInformation from './components/WitnessInformation';
import EvidenceUpload from './components/EvidenceUpload';
import InvestigatorAssignment from './components/InvestigatorAssignment';

// Shared in-memory store so the tracker can read released incidents
export const releasedIncidents = [];

const generateIncidentId = () => {
  const year = new Date().getFullYear();
  const seq = String(releasedIncidents.length + 6).padStart(3, '0'); // offset from mock data
  return `INC-${year}-${seq}`;
};

const IncidentCreationFormContent = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    classification: '',
    severity: '',
    injuryType: '',
    medicalTreatment: '',
    location: '',
    department: '',
    specificArea: '',
    workShift: '',
    incidentDate: '',
    incidentTime: '',
    reportedDate: new Date()?.toISOString()?.split('T')?.[0],
    reportedTime: new Date()?.toTimeString()?.split(' ')?.[0]?.substring(0, 5),
    personnelInvolved: [],
    description: '',
    immediateActions: '',
    rootCause: '',
    hazardCategories: [],
    customHazard: '',
    witnesses: [],
    documents: [],
    photos: [],
    assignedInvestigator: '',
    priority: 'medium'
  });

  const [expandedSections, setExpandedSections] = useState({
    classification: true,
    location: true,
    personnel: true,
    description: true,
    hazards: false,
    actions: false,
    witnesses: false,
    evidence: false,
    investigation: false
  });

  const [showDynamicFields, setShowDynamicFields] = useState({
    injuryDetails: false,
    fatalityDetails: false,
    medicalInfo: false
  });

  const [released, setReleased] = useState(false);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev?.[section]
    }));
  };

  const handleClassificationChange = (value) => {
    setFormData(prev => ({ ...prev, classification: value }));
    const isInjuryRelated = [
      'First Aid Case', 'Medical Treatment Case', 'Restricted Work Case',
      'Lost Time Injury', 'Permanent Disability'
    ]?.includes(value);
    const isFatality = value === 'Fatality';
    setShowDynamicFields({
      injuryDetails: isInjuryRelated,
      fatalityDetails: isFatality,
      medicalInfo: isInjuryRelated || isFatality
    });
  };

  const handleSeverityChange = (value) => {
    setFormData(prev => ({ ...prev, severity: value }));
    const requiresInjuryDetails = [
      'Minor Injury', 'Major Injury', 'Lost Time Injury',
      'Permanent Partial Disability', 'Permanent Total Disability'
    ]?.includes(value);
    const isFatality = value === 'Fatality';
    setShowDynamicFields(prev => ({
      ...prev,
      injuryDetails: requiresInjuryDetails,
      fatalityDetails: isFatality
    }));
  };

  const handleRelease = () => {
    if (!formData?.classification || !formData?.incidentDate || !formData?.location) {
      alert('Please fill in the required fields: Incident Type, Location, and Incident Date before releasing.');
      return;
    }

    const incidentId = generateIncidentId();
    const now = new Date().toISOString();

    const newInvestigation = {
      id: Date.now(),
      incidentId,
      title: formData?.description
        ? formData.description.substring(0, 80) + (formData.description.length > 80 ? '…' : '')
        : `${formData?.classification} — ${formData?.location}`,
      incidentType: ['Near Miss', 'Property Damage', 'Environmental Incident', 'Vehicle Incident'].includes(formData?.classification)
        ? 'near-miss'
        : formData?.classification === 'Fatality' ? 'fatality' : 'injury',
      severity: formData?.severity === 'Fatality' || formData?.classification === 'Fatality' ? 'critical'
        : formData?.severity === 'Lost Time Injury' || formData?.severity === 'Permanent Partial Disability' ? 'major'
        : formData?.severity === 'Major Injury' ? 'moderate' : 'minor',
      status: 'initiated',
      investigator: formData?.assignedInvestigator
        ? formData.assignedInvestigator === 'auto' ? 'Auto-assigned'
        : formData.assignedInvestigator === 'john' ? 'John Smith'
        : formData.assignedInvestigator === 'sarah' ? 'Sarah Johnson'
        : formData.assignedInvestigator === 'mike' ? 'Mike Wilson'
        : formData.assignedInvestigator === 'emily' ? 'Emily Davis' : 'Unassigned'
        : 'Unassigned',
      location: formData?.location,
      incidentDate: formData?.incidentDate ? new Date(formData.incidentDate).toISOString() : now,
      initiatedDate: now,
      targetCompletionDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
      progress: 0,
      recommendations: 0,
      implementedRecommendations: 0,
      documentsCollected: formData?.documents?.length || 0,
      interviewsCompleted: 0,
      rootCauseIdentified: false,
      priority: formData?.priority,
      releasedAt: now,
      formData: { ...formData },
    };

    releasedIncidents.push(newInvestigation);
    setReleased(true);

    setTimeout(() => {
      navigate('/incident-investigation-tracker');
    }, 1800);
  };

  const handleSubmit = (action) => {
    if (action === 'immediate') {
      alert('Incident reported immediately! Notifications sent to relevant personnel.');
    } else if (action === 'submit') {
      alert('Incident submitted for investigation!');
    } else {
      alert('Draft saved successfully!');
    }
    navigate('/incident-reporting-dashboard');
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? All unsaved changes will be lost.')) {
      navigate('/incident-reporting-dashboard');
    }
  };

  if (released) {
    return (
      <div className="bg-background min-h-screen flex items-center justify-center">
        <div className="text-center p-12">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-foreground mb-2">Incident Released</h2>
          <p className="text-muted-foreground mb-1">The incident has been released to the Investigation Tracker.</p>
          <p className="text-sm text-muted-foreground">Redirecting to Investigation Tracker…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background min-h-screen">
      <div className="p-6 lg:p-8">
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
                aria-label="Go back"
              >
                <Icon name="ArrowLeft" size={20} className="text-foreground" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-foreground">Report New Incident</h1>
                <p className="text-muted-foreground mt-1">
                  Document incident details for investigation and analysis
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              iconName="Home"
              onClick={() => navigate('/')}
            >
              Return to Dashboard
            </Button>
          </div>
        </div>

        <div className="space-y-4">
          {/* Classification Pathway - Accordion Cards */}
          <ClassificationPathway
            formData={formData}
            showDynamicFields={showDynamicFields}
          />

          {/* Classification Section */}
          <ClassificationSection
            expanded={expandedSections?.classification}
            onToggle={() => toggleSection('classification')}
            formData={formData}
            onClassificationChange={handleClassificationChange}
            onSeverityChange={handleSeverityChange}
            setFormData={setFormData}
            showDynamicFields={showDynamicFields}
          />

          {/* Location & Time Section */}
          <LocationTimeSection
            expanded={expandedSections?.location}
            onToggle={() => toggleSection('location')}
            formData={formData}
            setFormData={setFormData}
          />

          {/* Personnel Involved Section */}
          <PersonnelSection
            expanded={expandedSections?.personnel}
            onToggle={() => toggleSection('personnel')}
            formData={formData}
            setFormData={setFormData}
          />

          {/* Incident Description Section */}
          <IncidentDescription
            expanded={expandedSections?.description}
            onToggle={() => toggleSection('description')}
            formData={formData}
            setFormData={setFormData}
          />

          {/* Hazard Categories Section */}
          <HazardCategory
            expanded={expandedSections?.hazards}
            onToggle={() => toggleSection('hazards')}
            formData={formData}
            setFormData={setFormData}
          />

          {/* Immediate Actions Section */}
          <ImmediateActions
            expanded={expandedSections?.actions}
            onToggle={() => toggleSection('actions')}
            formData={formData}
            setFormData={setFormData}
          />

          {/* Witness Information Section */}
          <WitnessInformation
            expanded={expandedSections?.witnesses}
            onToggle={() => toggleSection('witnesses')}
            formData={formData}
            setFormData={setFormData}
          />

          {/* Evidence Upload Section */}
          <EvidenceUpload
            expanded={expandedSections?.evidence}
            onToggle={() => toggleSection('evidence')}
            formData={formData}
            setFormData={setFormData}
          />

          {/* Investigator Assignment Section */}
          <InvestigatorAssignment
            expanded={expandedSections?.investigation}
            onToggle={() => toggleSection('investigation')}
            formData={formData}
            setFormData={setFormData}
          />
        </div>

        {/* Action Buttons */}
        <div className="mt-8 bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-gray-900">Ready to submit?</p>
              <p className="text-xs text-gray-500">
                <strong>Release Incident</strong> sends it directly to the Investigation Tracker to begin the investigation workflow.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button
                variant="outline"
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button
                variant="outline"
                iconName="Save"
                onClick={() => handleSubmit('draft')}
              >
                Save Draft
              </Button>
              <Button
                variant="outline"
                iconName="AlertTriangle"
                onClick={() => handleSubmit('immediate')}
                className="border-orange-300 text-orange-700 hover:bg-orange-50"
              >
                Immediate Report
              </Button>
              <Button
                iconName="Send"
                onClick={handleRelease}
                className="bg-green-600 hover:bg-green-700 text-white border-green-600"
              >
                Release Incident
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Action Buttons */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 space-y-2">
          <Button
            className="w-full bg-green-600 hover:bg-green-700 text-white"
            onClick={handleRelease}
          >
            Release Incident
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={() => handleSubmit('draft')}>
              Save Draft
            </Button>
            <Button variant="outline" className="flex-1" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        </div>
        <div className="md:hidden h-32" />
      </div>
    </div>
  );
};

export default IncidentCreationFormContent;